import { Model, useRepo } from 'pinia-orm';
import { Attr, BelongsTo, Bool, HasMany, Num, Str } from 'pinia-orm/dist/decorators';
import Claim from './Claim';
import LumpSumCost from './LumpSumCost';
import PackageTradeItem from './PackageTradeItem';
import RatesCost from './RatesCost';
import Trade from './Trade';
import TradeItemCost from './TradeItemCost';
import VariationOrder from './VariationOrder';
/* --- user header --- */
/* --- end user header --- */

export default class TradeItem extends Model {
  static entity = 'App\\Models\\TradeItem';
  // fields
  @Num(0)
  declare id: number;
  @Str('')
  declare name: string;
  @Num(undefined)
  declare budget: number;
  @Num(undefined)
  declare quantity: number;
  @Num(undefined)
  declare rate: number;
  @Num(undefined)
  declare awarded_value: number;
  @Str('')
  declare unit: string;
  @Bool(false)
  declare has_children: boolean;
  @Bool(false)
  declare is_provisional_sum: boolean;
  @Num(0)
  declare sort_order: number;
  @Attr(undefined)
  declare bill_item: number | undefined;
  @Num(undefined)
  declare trade_id: number | undefined;
  @Num(undefined)
  declare parent_id: number | undefined;
  @Num(undefined)
  declare variation_order_id: number | undefined;
  @Str('')
  declare created_at: string | undefined;
  @Str('')
  declare updated_at: string | undefined;
  @Attr(undefined)
  declare $previous: number | undefined;
  // relations
  @BelongsTo(() => Trade, 'trade_id', 'id')
  declare trade: Trade;
  @BelongsTo(() => TradeItem, 'parent_id', 'id')
  declare parent: TradeItem;
  @HasMany(() => TradeItem, 'parent_id', 'id')
  declare children: TradeItem[];
  @HasMany(() => TradeItem, 'id', 'parent_id')
  declare ancestors: TradeItem[];
  @HasMany(() => TradeItem, 'parent_id', 'id')
  declare descendants: TradeItem[];
  @HasMany(() => TradeItemCost, 'trade_item_id', 'id')
  declare trade_item_costs: TradeItemCost[];
  @HasMany(() => RatesCost, 'trade_item_id', 'id')
  declare rates_costs: RatesCost[];
  @HasMany(() => PackageTradeItem, 'trade_item_id', 'id')
  declare package_trade_items: PackageTradeItem[];
  @BelongsTo(() => VariationOrder, 'variation_order_id', 'id')
  declare variation_order: VariationOrder;
  /* --- user code --- */
  @Attr(undefined)
  declare $totalCertified: number | undefined;
  @Attr(undefined)
  declare $totalClaim: number | undefined;
  @Attr(undefined)
  declare $profit: number | undefined;
  @Attr(undefined)
  declare $awarded_quantity: number | undefined;
  @Attr(undefined)
  declare $billItem: number | undefined;

  public previous(claim: Claim) {
    if(this.$previous !== undefined) {
      return this.$previous;
    }

    const previousCosts = useRepo(TradeItemCost)
      .where('trade_item_id', this.id)
      .whereHas('claim', (query) => {
        query.where('id', (id) => {
          return id !== claim.id;
        })
          .where('end', (end) => {
            return end < claim.end;
          });
      }).get();

    let totalPrevious = 0;

    previousCosts.forEach((cost) => {
      totalPrevious += cost.current_certified;
    });

    return totalPrevious;
  }

  get billNumber() {
    return this.$billItem ?? this.bill_item ?? this.sort_order;
  }

  public fullBillItem(items: TradeItem[]) {
    const billItemParts = [this.billNumber.toString()];
    // Walk up the parent hierarchy using parent_id
    let currentParentId = this.parent_id;

    while(currentParentId) {
      const parent = items.find((item) => item.id === currentParentId);

      if(parent) {
        billItemParts.unshift(parent.billNumber.toString());
        currentParentId = parent.parent_id;
      } else {
        // If parent not found in collection, stop traversing
        break;
      }
    }

    return billItemParts.join('.');
  }

  public childAdjustedBudget() {
    if(this.has_children) {
      const children = useRepo(TradeItem).where('parent_id', this.id).get();

      return children.reduce((total, current) => {
        return total + current.childAdjustedBudget();
      }, 0);
    }

    return this.budget;
  }

  public childBudget() {
    if(this.has_children) {
      const children = useRepo(TradeItem).where('parent_id', this.id).get();

      return children.reduce((total, current) => {
        return total + current.childBudget();
      }, 0);
    }

    return this.budget > 0 ? this.budget : 0;
  }

  public childBudgetExcludingProvisional() {
    if(this.has_children) {
      const children = useRepo(TradeItem).where('parent_id', this.id).get();

      return children.reduce((total, current) => {
        return total + current.childBudgetExcludingProvisional();
      }, 0);
    }

    return (this.budget > 0 && !this.is_provisional_sum) ? this.budget : 0;
  }

  public childProvisionalBudget() {
    if(this.has_children) {
      const children = useRepo(TradeItem).where('parent_id', this.id).get();

      return children.reduce((total, current) => {
        return total + current.childProvisionalBudget();
      }, 0);
    }

    return this.is_provisional_sum ? this.budget : 0;
  }

  public childAdjustedBudgetExcludingProvisional() {
    if(this.has_children) {
      const children = useRepo(TradeItem).where('parent_id', this.id).get();

      return children.reduce((total, current) => {
        return total + current.childAdjustedBudgetExcludingProvisional();
      }, 0);
    }

    return this.is_provisional_sum ? 0 : this.budget;
  }

  public childVariance(claim: Claim) {
    if(this.has_children) {
      const children = useRepo(TradeItem).where('parent_id', this.id).get();

      return children.reduce((total, current) => {
        return total + current.childVariance(claim);
      }, 0);
    }

    const tradeItemCost = useRepo(TradeItemCost).where('trade_item_id', this.id).where('claim_id', claim.id).first();

    return (tradeItemCost?.current_claim ?? 0) - (tradeItemCost?.current_certified ?? 0);
  }

  public childClaim(claim: Claim) {
    if(this.has_children) {
      const children = useRepo(TradeItem).where('parent_id', this.id).get();

      return children.reduce((total, current) => {
        return total + current.childClaim(claim);
      }, 0);
    }

    const tradeItemCost = useRepo(TradeItemCost).where('trade_item_id', this.id).where('claim_id', claim.id).first();

    return tradeItemCost?.current_claim ?? 0;
  }

  public childPrevious(claim: Claim) {
    if(this.has_children) {
      const children = useRepo(TradeItem).where('parent_id', this.id).get();

      return children.reduce((total, current) => {
        return total + current.childPrevious(claim);
      }, 0);
    }

    return this.previous(claim);
  }

  public childCertified(claim: Claim) {
    if(this.has_children) {
      const children = useRepo(TradeItem).where('parent_id', this.id).get();

      return children.reduce((total, current) => {
        return total + current.childCertified(claim);
      }, 0);
    }

    const tradeItemCost = useRepo(TradeItemCost).where('trade_item_id', this.id).where('claim_id', claim.id).first();

    return tradeItemCost?.current_certified ?? 0;
  }

  public childSubcontractSummaryClaim(lumpSumCostIds: Id[], ratesCostIds: Id[]) {
    if(this.has_children) {
      const children = useRepo(TradeItem).where('parent_id', this.id).get();

      return children.reduce((total, current) => {
        return total + current.childSubcontractSummaryClaim(lumpSumCostIds, ratesCostIds);
      }, 0);
    }

    let total = 0;

    useRepo(LumpSumCost)
      .where('trade_item_id', this.id)
      .whereIn('id', lumpSumCostIds)
      .get()
      .forEach((cost) => {
        total += cost.$summaryClaim ?? 0;
      });

    useRepo(RatesCost)
      .where('trade_item_id', this.id)
      .whereIn('id', ratesCostIds)
      .get()
      .forEach((cost) => {
        total += cost.$summaryClaim ?? 0;
      });

    return total;
  }

  public childSubcontractSummaryCertified(lumpSumCostIds: Id[], ratesCostIds: Id[]) {
    if(this.has_children) {
      const children = useRepo(TradeItem).where('parent_id', this.id).get();

      return children.reduce((sum, current) => {
        return sum + current.childSubcontractSummaryCertified(lumpSumCostIds, ratesCostIds);
      }, 0);
    }

    let total = 0;

    useRepo(LumpSumCost)
      .where('trade_item_id', this.id)
      .whereIn('id', lumpSumCostIds)
      .get()
      .forEach((cost) => {
        total += cost.$summaryCertified ?? 0;
      });

    useRepo(RatesCost)
      .where('trade_item_id', this.id)
      .whereIn('id', ratesCostIds)
      .get()
      .forEach((cost) => {
        total += cost.$summaryCertified ?? 0;
      });

    return total;
  }

  public isAssignedToPackage() {
    if(this.has_children) {
      const children = useRepo(TradeItem).where('parent_id', this.id).where('budget', (budget) => budget > 0).get();

      return children.every((child) => child.isAssignedToPackage());
    }

    const packageTradeItems = useRepo(PackageTradeItem).where('trade_item_id', this.id).get();

    return packageTradeItems.length > 0;
  }
  /* --- end user code --- */
}
